import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HeaderBar } from "@/components/grc/HeaderBar";
import { hasRole, useRole } from "@/context/RoleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Settings2, Tag, ChevronLeft, ChevronRight, Upload, Sparkles, Users, ScanSearch, Wand2 } from "lucide-react";
import { RoPACsvImport } from "@/components/ropa/RoPACsvImport";
import { RoPAConflictPanel } from "@/components/ropa/RoPAConflictPanel";
import { RoPAFlowMap } from "@/components/ropa/RoPAFlowMap";
import { RoPAReports } from "@/components/ropa/RoPANoticeBuilder";
import {
  ASSET_TYPES, CompletenessBar, CoverageGauge, DPA_STATUSES,
  HOSTINGS, PARTY_TYPES, PURPOSE_CATEGORIES, ragForPct, RISK_RATINGS, STATUSES,
  StatusPill, WorkspaceNav,
} from "@/components/ropa/shared";
import { fmtDate } from "@/components/grc/shared";

const ALL_COLUMNS = [
  { key: "record_ref", label: "Ref" },
  { key: "name", label: "Activity" },
  { key: "status", label: "Status" },
  { key: "purpose_category", label: "Purpose" },
  { key: "lawful_basis", label: "Lawful basis" },
  { key: "owner_name", label: "Owner" },
  { key: "owner_team_id", label: "Owner team" },
  { key: "completeness_pct", label: "Completeness" },
  { key: "next_review_date", label: "Next review" },
  { key: "business_function", label: "Function" },
  { key: "data_volume_band", label: "Volume" },
  { key: "involves_children", label: "Children" },
  { key: "cross_border", label: "Cross-border" },
  { key: "tags", label: "Tags" },
  { key: "updated_at", label: "Updated" },
];
const DEFAULT_COLUMNS = [
  "record_ref", "name", "status", "purpose_category", "lawful_basis",
  "owner_team_id", "completeness_pct", "next_review_date",
];

const KpiCard = ({ title, children, testId, muted }) => (
  <div
    data-testid={testId}
    className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${muted ? "opacity-60" : ""}`}
  >
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{title}</div>
    <div className="mt-2">{children}</div>
  </div>
);

export default function RoPARegister() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useRole();
  const canEdit = hasRole(role, "control_owner");
  const canReview = hasRole(role, "compliance_officer");
  const isAdmin = hasRole(role, "admin");

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [parties, setParties] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tab, setTab] = useState("activities");

  const [statusFilter, setStatusFilter] = useState(null);
  const [teamFilter, setTeamFilter] = useState(null);
  const [flagFilter, setFlagFilter] = useState({ involves_children: false, cross_border: false, stale_only: false });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [selected, setSelected] = useState([]);

  const [newRecord, setNewRecord] = useState({ open: false, name: "", business_function: "", purpose_category: "", owner_team_id: "" });
  const [newAsset, setNewAsset] = useState({ open: false, name: "", asset_type: "Application", hosting: "CloudIndia", hosting_region: "" });
  const [newParty, setNewParty] = useState({ open: false, name: "", party_type: "DataProcessor", country: "IN", dpa_status: "Missing", risk_rating: "Medium" });
  const [bulkTag, setBulkTag] = useState({ open: false, value: "" });
  const [bulkAssign, setBulkAssign] = useState({ open: false, teamId: "" });
  const [csvOpen, setCsvOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [conflictRefresh, setConflictRefresh] = useState(0);
  const [aiDraft, setAiDraft] = useState({ open: false, description: "", busy: false });

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/ropa/${id}/summary`);
      setSummary(data);
    } catch (e) {
      toast.error("Could not load RoPA summary");
    }
  }, [id]);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (statusFilter) params.status = statusFilter;
      if (teamFilter) params.owner_team_id = teamFilter;
      if (flagFilter.involves_children) params.involves_children = true;
      if (flagFilter.cross_border) params.cross_border = true;
      if (flagFilter.stale_only) params.stale_only = true;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get(`/ropa/${id}/activities`, { params });
      setRows(data.activities || []);
      setMeta({ total: data.total, page: data.page, pages: data.pages });
    } catch (e) {
      toast.error("Could not load RoPA records");
    } finally {
      setLoading(false);
    }
  }, [id, page, statusFilter, teamFilter, flagFilter, search]);

  const loadAssets = useCallback(async () => {
    const { data } = await api.get(`/ropa/${id}/assets`);
    setAssets(data.assets || []);
  }, [id]);

  const loadParties = useCallback(async () => {
    const { data } = await api.get(`/ropa/${id}/parties`);
    setParties(data.parties || []);
  }, [id]);

  useEffect(() => { loadSummary(); loadAssets(); loadParties(); }, [loadSummary, loadAssets, loadParties]);
  useEffect(() => { loadActivities(); }, [loadActivities]);
  useEffect(() => {
    api.get("/grc/teams").then(({ data }) => setTeams(data.teams || [])).catch(() => {});
  }, []);

  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => columns.includes(c.key)),
    [columns]
  );

  const cellValue = (row, key) => {
    switch (key) {
      case "status": return <StatusPill status={row.status} />;
      case "completeness_pct": return <CompletenessBar value={row.completeness_pct} />;
      case "next_review_date": return fmtDate(row.next_review_date);
      case "updated_at": return fmtDate(row.updated_at);
      case "involves_children": return row.involves_children ? "Yes" : "No";
      case "cross_border": return row.cross_border?.enabled ? "Yes" : "No";
      case "tags": return (row.tags || []).join(", ") || "—";
      case "owner_team_id": {
        const team = teams.find((t) => t.team_id === row.owner_team_id);
        return team?.name || team?.team_name || row.owner_team_id || "—";
      }
      case "name":
        return (
          <span className="flex items-center gap-2">
            <span className="font-medium text-[#003366]">{row.name}</span>
            {(row.open_conflicts || 0) > 0 && (
              <span
                data-testid={`ropa-conflict-badge-${row.record_ref}`}
                title={`${row.open_conflicts} open conflict(s)`}
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#D32F2F] text-white text-[10px] font-bold"
              >
                {row.open_conflicts}
              </span>
            )}
          </span>
        );
      default: return row[key] ?? "—";
    }
  };

  const createRecord = async () => {
    if (!newRecord.name.trim()) { toast.error("Activity name is required"); return; }
    try {
      const payload = { name: newRecord.name.trim() };
      if (newRecord.business_function) payload.business_function = newRecord.business_function;
      if (newRecord.purpose_category) payload.purpose_category = newRecord.purpose_category;
      if (newRecord.owner_team_id) payload.owner_team_id = newRecord.owner_team_id;
      const { data } = await api.post(`/ropa/${id}/activities`, payload);
      toast.success(`${data.record_ref} created`);
      setNewRecord({ open: false, name: "", business_function: "", purpose_category: "", owner_team_id: "" });
      navigate(`/assessment/${id}/ropa/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create record");
    }
  };

  const createAsset = async () => {
    if (!newAsset.name.trim()) { toast.error("Asset name is required"); return; }
    try {
      await api.post(`/ropa/${id}/assets`, {
        name: newAsset.name.trim(),
        asset_type: newAsset.asset_type,
        hosting: newAsset.hosting,
        hosting_region: newAsset.hosting_region || null,
      });
      toast.success("Asset added");
      setNewAsset({ open: false, name: "", asset_type: "Application", hosting: "CloudIndia", hosting_region: "" });
      loadAssets(); loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add asset");
    }
  };

  const createParty = async () => {
    if (!newParty.name.trim()) { toast.error("Party name is required"); return; }
    try {
      await api.post(`/ropa/${id}/parties`, {
        name: newParty.name.trim(),
        party_type: newParty.party_type,
        country: newParty.country || "IN",
        dpa_status: newParty.dpa_status,
        risk_rating: newParty.risk_rating,
      });
      toast.success("Party added");
      setNewParty({ open: false, name: "", party_type: "DataProcessor", country: "IN", dpa_status: "Missing", risk_rating: "Medium" });
      loadParties(); loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add party");
    }
  };

  const applyBulkTag = async () => {    const tags = bulkTag.value.split(",").map((t) => t.trim()).filter(Boolean);
    if (!tags.length) { toast.error("Enter at least one tag"); return; }
    try {
      await api.post(`/ropa/${id}/activities/bulk-tag`, { record_ids: selected, tags });
      toast.success(`Tagged ${selected.length} record(s)`);
      setBulkTag({ open: false, value: "" });
      setSelected([]);
      loadActivities();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk tag failed");
    }
  };

  const applyBulkAssign = async () => {
    if (!bulkAssign.teamId) { toast.error("Choose an owner team"); return; }
    try {
      const { data } = await api.post(`/ropa/${id}/activities/bulk-assign`, {
        record_ids: selected,
        owner_team_id: bulkAssign.teamId,
      });
      toast.success(`${data.modified} record(s) assigned to ${data.team_name || "team"}`);
      setBulkAssign({ open: false, teamId: "" });
      setSelected([]);
      loadActivities();
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assignment failed");
    }
  };

  const [analyzing, setAnalyzing] = useState(false);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/ropa/${id}/analyze`);
      toast.success(
        `${data.conflicts_found} conflict(s) — ${data.findings_created} new finding(s), ` +
        `${data.findings_closed} auto-closed, ${data.tasks_created} task(s) created`
      );
      setConflictRefresh((n) => n + 1);
      loadActivities();
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const createAiDraft = async () => {
    if (aiDraft.description.trim().length < 5) { toast.error("Describe the processing activity first"); return; }
    setAiDraft((a) => ({ ...a, busy: true }));
    try {
      const { data } = await api.post(`/ropa/${id}/ai/draft-activity`, { description: aiDraft.description.trim() });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`${data.record_ref} drafted — confirm the AI fields before submitting`);
      setAiDraft({ open: false, description: "", busy: false });
      navigate(`/assessment/${id}/ropa/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed");
      setAiDraft((a) => ({ ...a, busy: false }));
    }
  };

  const seedFromIndustry = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post(`/ropa/${id}/seed`);
      toast.success(`${data.pack} pack — ${data.created} draft(s) created, ${data.skipped} skipped`);
      setPage(1);
      loadActivities();
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  const afterCsvCommit = () => { setPage(1); loadActivities(); loadSummary(); loadAssets(); loadParties(); };

  const noRecords = !loading && meta.total === 0 && !search && !statusFilter && !teamFilter;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ropa-register-page">
      <HeaderBar assessmentId={id} />
      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 space-y-6">
        <WorkspaceNav assessmentId={id} />

        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ropa-kpi-strip">
          <KpiCard title="RoPA Coverage" testId="ropa-kpi-coverage">
            <div className="flex items-center gap-4">
              <CoverageGauge value={summary?.ropa_coverage_pct} />
              <p className="text-[11px] text-slate-500 leading-relaxed max-w-[140px]">
                Tracked independently — never folded into your DPDP compliance score.
              </p>
            </div>
          </KpiCard>
          <KpiCard title="Approved / Total" testId="ropa-kpi-approved">
            <div className="text-3xl font-bold text-[#003366]" data-testid="ropa-approved-count">
              {summary?.approved ?? 0}
              <span className="text-lg text-slate-400 font-medium"> / {summary?.total ?? 0}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Mean completeness {summary?.mean_completeness ?? 0}%
            </div>
          </KpiCard>
          <KpiCard title="Open Conflicts" testId="ropa-kpi-conflicts">
            <div
              className="text-3xl font-bold"
              style={{ color: (summary?.open_conflicts ?? 0) > 0 ? "#D32F2F" : "#388E3C" }}
              data-testid="ropa-conflicts-count"
            >
              {summary?.open_conflicts ?? 0}
            </div>
            <button
              onClick={() => setTab("conflicts")}
              data-testid="ropa-view-conflicts-link"
              className="text-xs text-slate-500 mt-1 hover:text-[#008080] underline"
            >
              {(summary?.open_conflicts ?? 0) > 0 ? "Review DPDP rule conflicts" : "No rule conflicts detected"}
            </button>
          </KpiCard>
          <KpiCard title="Stale Records" testId="ropa-kpi-stale">
            <div className="text-3xl font-bold text-[#F57C00]" data-testid="ropa-stale-count">
              {summary?.stale_count ?? 0}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {summary?.parties_missing_dpa ?? 0} parties without a signed DPA
            </div>
          </KpiCard>
        </section>

        {(summary?.by_owner_team?.length || 0) > 0 && (
          <section
            className="bg-white border border-slate-200 rounded-xl p-5"
            data-testid="ropa-team-completion"
          >
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                Completion by owner team
              </h2>
              <span className="text-[11px] text-slate-400">Click a team to filter the register</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-3">
              {summary.by_owner_team.map((t) => {
                const key = t.owner_team_id || "unassigned";
                const active = teamFilter === t.owner_team_id;
                return (
                  <button
                    key={key}
                    data-testid={`ropa-team-bar-${key}`}
                    onClick={() => {
                      setTeamFilter(active ? null : t.owner_team_id);
                      setTab("activities");
                      setPage(1);
                    }}
                    className={`text-left group rounded-lg px-2 py-1.5 -mx-2 transition-colors ${
                      active ? "bg-slate-100" : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className={`truncate ${t.owner_team_id ? "text-slate-700" : "text-slate-400 italic"}`}>
                        {t.team_name}
                      </span>
                      <span className="text-slate-400 shrink-0 font-mono text-[11px]">
                        {t.approved}/{t.total} approved · {t.mean_completeness}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{
                          width: `${Math.max(2, Math.min(100, t.mean_completeness))}%`,
                          backgroundColor: ragForPct(t.mean_completeness),
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <Tabs value={tab} onValueChange={setTab}>          <TabsList className="bg-white border border-slate-200 h-auto p-1 flex flex-wrap justify-start">
            {[
              ["activities", "Activities"], ["assets", "Assets"], ["parties", "Parties"],
              ["conflicts", "Conflicts"], ["dataflow", "Data Flow"], ["reports", "Reports"],
            ].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`ropa-tab-${v}`} className="text-xs md:text-sm">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ------------------------------ Activities ------------------------------ */}
          <TabsContent value="activities" className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="ropa-search-input"
                  placeholder="Search records…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-8 h-9 w-[230px] bg-white"
                />
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="ropa-filter-chips">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`ropa-chip-${s}`}
                    onClick={() => { setStatusFilter(statusFilter === s ? null : s); setPage(1); }}
                    className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                      statusFilter === s
                        ? "bg-[#003366] text-white border-[#003366]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    {s}
                  </button>
                ))}
                {[["involves_children", "Children"], ["cross_border", "Cross-border"], ["stale_only", "Stale only"]].map(                  ([k, l]) => (
                    <button
                      key={k}
                      data-testid={`ropa-chip-${k}`}
                      onClick={() => { setFlagFilter((f) => ({ ...f, [k]: !f[k] })); setPage(1); }}
                      className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                        flagFilter[k]
                          ? "bg-[#00897B] text-white border-[#00897B]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {l}
                    </button>
                  )
                )}
              </div>
              {teamFilter && (
                <button
                  data-testid="ropa-team-filter-chip"
                  onClick={() => { setTeamFilter(null); setPage(1); }}
                  className="px-2.5 py-1 rounded-full border border-[#003366] bg-[#003366] text-white text-[11px] font-medium"
                >
                  Team: {teams.find((t) => t.team_id === teamFilter)?.name || teamFilter} ×
                </button>
              )}

              <div className="ml-auto flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="ropa-columns-button" className="bg-white">
                      <Settings2 size={14} className="mr-1.5" /> Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-[320px] overflow-y-auto">
                    <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
                    {ALL_COLUMNS.map((c) => (
                      <DropdownMenuCheckboxItem
                        key={c.key}
                        checked={columns.includes(c.key)}
                        data-testid={`ropa-column-toggle-${c.key}`}
                        onCheckedChange={(v) =>
                          setColumns((prev) => (v ? [...prev, c.key] : prev.filter((k) => k !== c.key)))
                        }
                      >
                        {c.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {canEdit && selected.length > 0 && (
                  <Dialog open={bulkAssign.open} onOpenChange={(o) => setBulkAssign((b) => ({ ...b, open: o }))}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="ropa-bulk-assign-button" className="bg-white">
                        <Users size={14} className="mr-1.5" /> Assign team ({selected.length})
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="ropa-bulk-assign-dialog">
                      <DialogHeader><DialogTitle>Assign owner team to {selected.length} record(s)</DialogTitle>
                        <DialogDescription>The team becomes accountable for completing and maintaining each record.</DialogDescription></DialogHeader>
                      <Label className="text-xs">Owner team</Label>
                      <Select value={bulkAssign.teamId} onValueChange={(v) => setBulkAssign((b) => ({ ...b, teamId: v }))}>
                        <SelectTrigger data-testid="ropa-bulk-assign-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                        <SelectContent>
                          {teams.map((t) => (
                            <SelectItem key={t.team_id} value={t.team_id}>{t.name || t.team_name || t.team_id}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <DialogFooter>
                        <Button onClick={applyBulkAssign} data-testid="ropa-bulk-assign-submit" className="bg-[#008080] hover:bg-[#1a51db]">
                          Assign team
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {canEdit && selected.length > 0 && (
                  <Dialog open={bulkTag.open} onOpenChange={(o) => setBulkTag((b) => ({ ...b, open: o }))}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="ropa-bulk-tag-button" className="bg-white">
                        <Tag size={14} className="mr-1.5" /> Tag {selected.length}
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="ropa-bulk-tag-dialog">
                      <DialogHeader><DialogTitle>Bulk tag {selected.length} record(s)</DialogTitle>
                        <DialogDescription>Tags are added to every selected record; existing tags are kept.</DialogDescription></DialogHeader>
                      <Label className="text-xs">Tags (comma separated)</Label>
                      <Input
                        data-testid="ropa-bulk-tag-input"
                        value={bulkTag.value}
                        onChange={(e) => setBulkTag((b) => ({ ...b, value: e.target.value }))}
                        placeholder="hr, payroll"
                      />
                      <DialogFooter>
                        <Button onClick={applyBulkTag} data-testid="ropa-bulk-tag-submit" className="bg-[#008080] hover:bg-[#1a51db]">
                          Apply tags
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {canReview && (
                  <Button
                    variant="outline" size="sm" data-testid="ropa-run-analysis-button"
                    className="bg-white" onClick={runAnalysis} disabled={analyzing}
                  >
                    <ScanSearch size={14} className="mr-1.5" /> {analyzing ? "Analysing…" : "Run analysis"}
                  </Button>
                )}

                {canReview && (
                  <Button
                    variant="outline" size="sm" data-testid="ropa-import-csv-button"
                    className="bg-white" onClick={() => setCsvOpen(true)}
                  >
                    <Upload size={14} className="mr-1.5" /> Import CSV
                  </Button>
                )}

                {isAdmin && (
                  <Button
                    variant="outline" size="sm" data-testid="ropa-seed-industry-button"
                    className="bg-white" onClick={seedFromIndustry} disabled={seeding}
                  >
                    <Sparkles size={14} className="mr-1.5" /> {seeding ? "Seeding…" : "Seed from Industry"}
                  </Button>
                )}

                {canEdit && (
                  <Dialog open={aiDraft.open} onOpenChange={(o) => setAiDraft((a) => ({ ...a, open: o }))}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="ropa-ai-draft-button" className="bg-white">
                        <Wand2 size={14} className="mr-1.5" /> AI Draft
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="ropa-ai-draft-dialog">
                      <DialogHeader><DialogTitle>Draft a record with AI</DialogTitle>
                        <DialogDescription>
                          Claude drafts the DPDP fields; every suggested field must be confirmed before the record can be submitted.
                        </DialogDescription></DialogHeader>
                      <Label className="text-xs">Describe the processing activity</Label>
                      <Input
                        data-testid="ropa-ai-draft-input"
                        value={aiDraft.description}
                        onChange={(e) => setAiDraft((a) => ({ ...a, description: e.target.value }))}
                        placeholder="We record support calls to resolve customer disputes"
                      />
                      <DialogFooter>
                        <Button onClick={createAiDraft} disabled={aiDraft.busy} data-testid="ropa-ai-draft-submit" className="bg-[#008080] hover:bg-[#1a51db]">
                          {aiDraft.busy ? "Drafting…" : "Draft record"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {canEdit && (
                  <Dialog open={newRecord.open} onOpenChange={(o) => setNewRecord((n) => ({ ...n, open: o }))}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="ropa-new-record-button" className="bg-[#008080] hover:bg-[#1a51db]">
                        <Plus size={14} className="mr-1.5" /> New Record
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="ropa-new-record-dialog">
                      <DialogHeader><DialogTitle>New processing activity</DialogTitle>
                        <DialogDescription>Start with the basics — you can complete every DPDP field on the record page.</DialogDescription></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Activity name *</Label>
                          <Input
                            data-testid="ropa-new-record-name"
                            value={newRecord.name}
                            onChange={(e) => setNewRecord((n) => ({ ...n, name: e.target.value }))}
                            placeholder="Customer onboarding KYC"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Business function</Label>
                          <Input
                            data-testid="ropa-new-record-function"
                            value={newRecord.business_function}
                            onChange={(e) => setNewRecord((n) => ({ ...n, business_function: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Purpose category</Label>
                          <Select
                            value={newRecord.purpose_category}
                            onValueChange={(v) => setNewRecord((n) => ({ ...n, purpose_category: v }))}
                          >
                            <SelectTrigger data-testid="ropa-new-record-purpose"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {PURPOSE_CATEGORIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Owner team</Label>
                          <Select
                            value={newRecord.owner_team_id}
                            onValueChange={(v) => setNewRecord((n) => ({ ...n, owner_team_id: v }))}
                          >
                            <SelectTrigger data-testid="ropa-new-record-team"><SelectValue placeholder="Select team" /></SelectTrigger>
                            <SelectContent>
                              {teams.map((t) => (
                                <SelectItem key={t.team_id} value={t.team_id}>{t.name || t.team_name || t.team_id}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={createRecord} data-testid="ropa-new-record-submit" className="bg-[#008080] hover:bg-[#1a51db]">
                          Create record
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            {loading && <Skeleton className="h-72 rounded-xl" />}

            {noRecords && !loading && (
              <div className="rounded-xl border border-slate-200 bg-white py-20 text-center space-y-5" data-testid="ropa-empty-state">
                <div className="text-sm text-slate-600">No processing activities recorded for this assessment yet.</div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {isAdmin && (
                    <Button
                      onClick={seedFromIndustry} disabled={seeding}
                      data-testid="ropa-empty-seed-cta"
                      className="bg-[#003366] hover:bg-[#003366]/90"
                    >
                      <Sparkles size={15} className="mr-1.5" /> {seeding ? "Seeding…" : "Seed from my industry"}
                    </Button>
                  )}
                  {canReview && (
                    <Button variant="outline" onClick={() => setCsvOpen(true)} data-testid="ropa-empty-csv-cta">
                      <Upload size={15} className="mr-1.5" /> Import CSV
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="outline"
                      onClick={() => setNewRecord((n) => ({ ...n, open: true }))}
                      data-testid="ropa-empty-cta"
                    >
                      Create manually
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!loading && !noRecords && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ropa-activities-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {canEdit && <th className="w-10 px-3 py-2.5" />}
                        {visibleColumns.map((c) => (
                          <th key={c.key} className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold whitespace-nowrap">
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.id}
                          data-testid={`ropa-row-${r.record_ref}`}
                          className="border-b border-slate-100 hover:bg-slate-50/70 cursor-pointer transition-colors"
                          onClick={() => navigate(`/assessment/${id}/ropa/${r.id}`)}
                        >
                          {canEdit && (
                            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                data-testid={`ropa-select-${r.record_ref}`}
                                checked={selected.includes(r.id)}
                                onCheckedChange={(v) =>
                                  setSelected((s) => (v ? [...s, r.id] : s.filter((x) => x !== r.id)))
                                }
                              />
                            </td>
                          )}
                          {visibleColumns.map((c) => (
                            <td key={c.key} className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                              {cellValue(r, c.key)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={visibleColumns.length + 1} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="ropa-no-match">
                            No records match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
                  <span data-testid="ropa-pagination-meta">
                    Page {meta.page} of {meta.pages} · {meta.total} record(s)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm" data-testid="ropa-prev-page"
                      disabled={meta.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft size={14} />
                    </Button>
                    <Button
                      variant="outline" size="sm" data-testid="ropa-next-page"
                      disabled={meta.page >= meta.pages} onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ------------------------------ Assets ------------------------------ */}
          <TabsContent value="assets" className="mt-4 space-y-3">
            {canEdit && (
              <div className="flex justify-end">
                <Dialog open={newAsset.open} onOpenChange={(o) => setNewAsset((n) => ({ ...n, open: o }))}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="ropa-new-asset-button" className="bg-[#003366] hover:bg-[#003366]/90">
                      <Plus size={14} className="mr-1.5" /> New Asset
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="ropa-new-asset-dialog">
                    <DialogHeader><DialogTitle>New asset / system</DialogTitle>
                      <DialogDescription>Register the systems that hold personal data so activities can link to them.</DialogDescription></DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Name *</Label>
                        <Input data-testid="ropa-new-asset-name" value={newAsset.name}
                          onChange={(e) => setNewAsset((n) => ({ ...n, name: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs">Asset type</Label>
                        <Select value={newAsset.asset_type} onValueChange={(v) => setNewAsset((n) => ({ ...n, asset_type: v }))}>
                          <SelectTrigger data-testid="ropa-new-asset-type"><SelectValue /></SelectTrigger>
                          <SelectContent>{ASSET_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Hosting</Label>
                        <Select value={newAsset.hosting} onValueChange={(v) => setNewAsset((n) => ({ ...n, hosting: v }))}>
                          <SelectTrigger data-testid="ropa-new-asset-hosting"><SelectValue /></SelectTrigger>
                          <SelectContent>{HOSTINGS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Hosting region</Label>
                        <Input data-testid="ropa-new-asset-region" value={newAsset.hosting_region}
                          onChange={(e) => setNewAsset((n) => ({ ...n, hosting_region: e.target.value }))} placeholder="ap-south-1" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={createAsset} data-testid="ropa-new-asset-submit" className="bg-[#008080] hover:bg-[#1a51db]">
                        Add asset
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="ropa-assets-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["Name", "Type", "Hosting", "Region", "Personal data", "Linked activities"].map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100" data-testid={`ropa-asset-row-${a.id}`}>
                      <td className="px-3 py-2.5 font-medium text-[#003366]">{a.name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{a.asset_type}</td>
                      <td className="px-3 py-2.5 text-slate-600">{a.hosting}</td>
                      <td className="px-3 py-2.5 text-slate-600">{a.hosting_region || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{a.contains_personal_data ? "Yes" : "No"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{a.linked_activity_count ?? 0}</td>
                    </tr>
                  ))}
                  {assets.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="ropa-assets-empty">
                      No assets registered yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ------------------------------ Parties ------------------------------ */}
          <TabsContent value="parties" className="mt-4 space-y-3">
            {canEdit && (
              <div className="flex justify-end">
                <Dialog open={newParty.open} onOpenChange={(o) => setNewParty((n) => ({ ...n, open: o }))}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="ropa-new-party-button" className="bg-[#003366] hover:bg-[#003366]/90">
                      <Plus size={14} className="mr-1.5" /> New Party
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="ropa-new-party-dialog">
                    <DialogHeader><DialogTitle>New processor / recipient</DialogTitle>
                      <DialogDescription>Record third parties that receive or process personal data on your behalf.</DialogDescription></DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Name *</Label>
                        <Input data-testid="ropa-new-party-name" value={newParty.name}
                          onChange={(e) => setNewParty((n) => ({ ...n, name: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs">Party type</Label>
                        <Select value={newParty.party_type} onValueChange={(v) => setNewParty((n) => ({ ...n, party_type: v }))}>
                          <SelectTrigger data-testid="ropa-new-party-type"><SelectValue /></SelectTrigger>
                          <SelectContent>{PARTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Country (ISO-2)</Label>
                        <Input data-testid="ropa-new-party-country" value={newParty.country}
                          onChange={(e) => setNewParty((n) => ({ ...n, country: e.target.value.toUpperCase().slice(0, 2) }))} />
                      </div>
                      <div>
                        <Label className="text-xs">DPA status</Label>
                        <Select value={newParty.dpa_status} onValueChange={(v) => setNewParty((n) => ({ ...n, dpa_status: v }))}>
                          <SelectTrigger data-testid="ropa-new-party-dpa"><SelectValue /></SelectTrigger>
                          <SelectContent>{DPA_STATUSES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Risk rating</Label>
                        <Select value={newParty.risk_rating} onValueChange={(v) => setNewParty((n) => ({ ...n, risk_rating: v }))}>
                          <SelectTrigger data-testid="ropa-new-party-risk"><SelectValue /></SelectTrigger>
                          <SelectContent>{RISK_RATINGS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={createParty} data-testid="ropa-new-party-submit" className="bg-[#008080] hover:bg-[#1a51db]">
                        Add party
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="ropa-parties-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["Name", "Type", "Country", "DPA", "Risk", "Linked activities"].map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parties.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`ropa-party-row-${p.id}`}>
                      <td className="px-3 py-2.5 font-medium text-[#003366]">{p.name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.party_type}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.country}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.dpa_status}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.risk_rating}</td>
                      <td className="px-3 py-2.5 text-slate-600">{p.linked_activity_count ?? 0}</td>
                    </tr>
                  ))}
                  {parties.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="ropa-parties-empty">
                      No third parties registered yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="conflicts" className="mt-4">
            <RoPAConflictPanel key={conflictRefresh} assessmentId={id} onChanged={loadSummary} />
          </TabsContent>
          <TabsContent value="dataflow" className="mt-4">
            <RoPAFlowMap
              assessmentId={id}
              onNodeSelect={(node) => {
                if (node?.column === 1 || node?.column === 2) {
                  setSearch("");
                  setTab("activities");
                  toast.info(`Filtering the register by ${node.name}`);
                  setSearch(node.name);
                  setPage(1);
                }
              }}
            />
          </TabsContent>
          <TabsContent value="reports" className="mt-4">
            <RoPAReports assessmentId={id} canExportDocx={canReview} />
          </TabsContent>
        </Tabs>

        <RoPACsvImport
          assessmentId={id}
          open={csvOpen}
          onOpenChange={setCsvOpen}
          onCommitted={afterCsvCommit}
          canCommit={isAdmin}
        />
      </main>
    </div>
  );
}
